import { GAME_ASSET_SLUG } from "@/config/game";
import { DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { Switch } from "@/design-system/Switch/Switch";
import { type TranslateFn, useTranslation } from "@repo/shared/i18n";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  metalIcon,
  pastAndPresentIcon,
  rarityIcon,
} from "../../../../../designSystemIcons";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { loadedContentSelector } from "../../../../../redux/loadedContent/loadedContentSlice";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { FilterDropdown, toClubItems } from "../FilterDropdown/FilterDropdown";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  selectedPnPs: number[];
  selectedVersions: number[];
  selectedMetals: number[];
  excludeIconsHeroes: boolean;
  handleClickedClose: () => void;
  handleSelectedPnP: (id: number) => void;
  handleSelectedVersion: (id: number) => void;
  handleExcludeIconsHeroesUpdated: (excludeIconsHeroes: boolean) => void;
  handleSelectedMetal: (id: number) => void;
  includeLoanPlayers: boolean;
  handleIncludeLoanPlayersUpdated: (includeLoanPlayers: boolean) => void;
};

const buildMetalOptions = (t: TranslateFn<"squadBuilder">): DropdownItem[] => [
  {
    value: "0",
    label: t("moreFilters.gold"),
    icon: (
      <img
        alt={t("moreFilters.gold")}
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_3.png?scale=50.png`}
      />
    ),
  },
  {
    value: "1",
    label: t("moreFilters.silver"),
    icon: (
      <img
        alt={t("moreFilters.silver")}
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_2.png?scale=50.png`}
      />
    ),
  },
  {
    value: "2",
    label: t("moreFilters.bronze"),
    icon: (
      <img
        alt={t("moreFilters.bronze")}
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_1.png?scale=50.png`}
      />
    ),
  },
];

export const MoreFiltersMode: React.FC<Props> = ({
  selectedPnPs,
  selectedVersions,
  selectedMetals,
  excludeIconsHeroes,
  includeLoanPlayers,
  handleClickedClose,
  handleSelectedPnP,
  handleSelectedVersion,
  handleExcludeIconsHeroesUpdated,
  handleIncludeLoanPlayersUpdated,
  handleSelectedMetal,
}) => {
  const { clubs: loadedClubs, versions: loadedVersions } = useSelector(
    loadedContentSelector,
  );
  const { t } = useTranslation("squadBuilder");
  const ref = useOutsideClick(handleClickedClose);

  const pnps = useMemo(() => toClubItems(loadedClubs), [loadedClubs]);
  const versions = useMemo<DropdownItem[]>(
    () =>
      loadedVersions.map((version) => ({
        value: String(version.id),
        label: version.name,
        icon: (
          <img
            className="w-5 h-5 my-auto"
            src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/cards/e_${
              version.id
            }_${version.hasLevels ? 3 : 0}.png`}
            alt={version.name}
          />
        ),
      })),
    [loadedVersions],
  );
  const metals = useMemo(() => buildMetalOptions(t), [t]);

  const toggles = [
    {
      label: t("moreFilters.excludeIconsHeroes"),
      isActive: excludeIconsHeroes,
      onChange: handleExcludeIconsHeroesUpdated,
    },
    {
      label: t("moreFilters.includeLoanPlayers"),
      isActive: includeLoanPlayers,
      onChange: handleIncludeLoanPlayersUpdated,
    },
  ];

  return (
    <div className="h-screen mobile:h-auto" ref={ref}>
      <PanelHeader
        title={t("panel.moreGeneralFilters")}
        onClose={handleClickedClose}
      />
      <PanelContainer>
        <ComponentContainer marginBottom="mb-[350px] mt-8">
          <div className="flex flex-col gap-y-2">
            <FilterDropdown
              label={t("moreFilters.rarity")}
              items={versions}
              selected={selectedVersions}
              onSelect={handleSelectedVersion}
              placeholderIcon={rarityIcon}
            />
            <FilterDropdown
              label={t("moreFilters.metal")}
              items={metals}
              selected={selectedMetals}
              onSelect={handleSelectedMetal}
              placeholderIcon={metalIcon}
              hideSearch
            />
            <FilterDropdown
              label={t("moreFilters.pastAndPresent")}
              items={pnps}
              selected={selectedPnPs}
              onSelect={handleSelectedPnP}
              placeholderIcon={pastAndPresentIcon}
            />
            <div className="flex flex-col mt-2 gap-y-3">
              {toggles.map(({ label, isActive, onChange }) => (
                <div
                  key={label}
                  className="flex items-center justify-between gap-x-2"
                >
                  <span className="ml-3 text-xs text-foreground-body">
                    {label}
                  </span>
                  <Switch isActive={isActive} onChange={onChange} />
                </div>
              ))}
            </div>
          </div>
        </ComponentContainer>
      </PanelContainer>
    </div>
  );
};
